import styled from '@emotion/styled';
import {
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Casino as CasinoIcon,
} from '@mui/icons-material';
import { Chip, ChipProps, Tooltip } from '@mui/material';
import { rgba } from 'polished';
import React, { FC, Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CopyText, Money } from 'components';
import { TranslationNamespace } from 'i18n';
import { PayinOrder, Order } from 'types';
import { orderUtils } from 'utils';

const Label = styled(Chip)<ChipProps>`
  background: ${(props) => rgba(props.theme.palette.success.main, 0.1)};
  border: 0;
`;

const RandomizationIcon: FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.table.tooltips',
  });
  return (
    <Tooltip title={t('randomization_applied')} arrow placement="top">
      <CasinoIcon className="tw-mx-1" fontSize="small" color="disabled" />
    </Tooltip>
  );
};

type Props = {
  order: Order;
};

export const OrderAmountColumn: React.FC<Props> = ({ order }) => {
  const payinOrder = useMemo(() => order as PayinOrder, [order]);

  const hasRandomization = useMemo(
    () =>
      payinOrder.initialAmount &&
      payinOrder.initialAmount !== payinOrder.amount &&
      !payinOrder.previousAmount,
    [payinOrder.initialAmount, payinOrder.amount, payinOrder.previousAmount],
  );

  const randomizedAndChangedAmount = useMemo(() => {
    if (
      payinOrder.initialAmount &&
      payinOrder.previousAmount &&
      payinOrder.initialAmount !== payinOrder.previousAmount
    ) {
      return payinOrder.previousAmount;
    }
    return null;
  }, [payinOrder.initialAmount, payinOrder.previousAmount]);

  const previousAmountToDisplay = useMemo(() => {
    // changed in dispute (no randomization)
    if (payinOrder.previousAmount && !randomizedAndChangedAmount) {
      return payinOrder.previousAmount;
    }
    // changed with randomization (no dispute)
    if (hasRandomization) {
      return payinOrder.initialAmount;
    }
    // both randomization and dispute
    if (randomizedAndChangedAmount) {
      return randomizedAndChangedAmount;
    }
    return null;
  }, [
    payinOrder.previousAmount,
    payinOrder.initialAmount,
    hasRandomization,
    randomizedAndChangedAmount,
  ]);

  return (
    <Fragment>
      <div className="tw-ml-2 tw-flex tw-items-center">
        <span>
          {previousAmountToDisplay && (
            <>
              {previousAmountToDisplay}
              {randomizedAndChangedAmount && <RandomizationIcon />}
              {' → '}
            </>
          )}
          <Money
            value={order.amount}
            fiatCurrencyId={order.fiatCurrencyId}
            symbol
          />
          {hasRandomization && <RandomizationIcon />}
        </span>
        <CopyText
          className="tw-mb-1"
          text={String(order.amount)}
          label={<span />}
        />
      </div>
      {orderUtils.isCrossFiatPayment(order) && (
        <Fragment>
          <div className="tw-ml-2">
            <CopyText
              text={String(order.crossFiatCurrencyAmount)}
              label={
                <span className="tw-flex">
                  {'~'}
                  <Money
                    value={order.crossFiatCurrencyAmount}
                    fiatCurrencyId={order.crossFiatCurrencyId}
                    symbol
                  />
                </span>
              }
            />
          </div>
        </Fragment>
      )}
      <div className="tw-flex tw-items-end">
        <Label
          label={
            <div>
              <AccountBalanceWalletIcon color="success" fontSize="small" />
              <Money
                value={order.assetCurrencyAmount}
                assetCurrencyId={order.assetCurrencyId}
                symbol
              />
            </div>
          }
          size="small"
          variant="outlined"
        />
        <CopyText
          className="tw-inline"
          text={String(order.assetCurrencyAmount)}
          iconOnly
        />
      </div>
    </Fragment>
  );
};
